import {
  ALLOWED_SERVICES_SLUG,
  DEFAULT_LIMIT,
  DEFAULT_THEME,
  MAX_COOKIE_SIZE,
  type TServiciosSlug,
} from '~~/constants';

/**
 * Runtime-safe preferences for the persisted `mb_user` cookie.
 *
 * The persisted value is untrusted (user-editable JSON in a cookie), so every
 * field is validated before use. This shape is compatible with the
 * `UserPreferences` type from `~~/stores/userconfig` but is kept local to keep
 * this module pure (no Nuxt/Pinia/Vue imports, no storage access).
 */
export type UserPreferences = {
  estaciones: Record<string, string[]>;
  limite: number;
  servicio: TServiciosSlug;
  theme: 'dark' | 'light';
};

/**
 * Raw persisted shape of the `mb_user` cookie, shared by the store and the
 * middleware. `servicio` is nullable so the store can distinguish "cookie
 * absent / no saved service" from "saved service equals the base service"
 * without conflating the two at the default boundary.
 *
 * `estaciones` maps each service slug to its saved station references (slugs
 * or ids) and is the current cookie field that holds the former
 * `mb_estaciones` data.
 *
 * Persistence contract (enforced by the size-bounding helpers, not by this
 * type):
 * - The stored value is sized as
 *   `encodeURIComponent(JSON.stringify(fullPreferences)).length`; cookie
 *   attributes are excluded because they are not part of the value.
 * - The value is capped at `MAX_COOKIE_SIZE` bytes; overflow evicts station
 *   references oldest-first (FIFO).
 * - FIFO order is the service insertion order (object key order), then the
 *   array order within each service's reference list.
 * - Station references are deduplicated per service, keeping the first
 *   occurrence.
 * - Core fields (`servicio`, `theme`, `limite`) are never evicted: if they
 *   alone exceed `MAX_COOKIE_SIZE`, the write is rejected rather than
 *   truncated.
 * - Malformed persisted input yields a failure result from the helpers; it
 *   never throws.
 */
export type StoredUserPreferences = {
  estaciones: Record<string, string[]>;
  limite: number;
  servicio: TServiciosSlug | null;
  theme: 'dark' | 'light';
};

/** Minimal station metadata needed to recognise saved station refs. */
export interface StationRef {
  _id: string | number;
  slug?: string;
  nom?: string;
  name?: string;
}

function slugFromStationName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const allowedServiceSlugs = new Set<string>(ALLOWED_SERVICES_SLUG);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Accepts a raw JSON string or an already-parsed value; never throws. */
function parseStoredJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  if (!raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isStationRefString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isValidServicioSlug(value: string): value is TServiciosSlug {
  return allowedServiceSlugs.has(value);
}

/**
 * Validates a service slug against `ALLOWED_SERVICES_SLUG`.
 * Returns the slug typed as `TServiciosSlug`, or null for unknown/empty values.
 */
export function validateServicioSlug(value: unknown): TServiciosSlug | null {
  if (typeof value !== 'string') return null;
  const slug = value.trim();
  if (!slug) return null;
  if (!isValidServicioSlug(slug)) return null;
  return slug;
}

function parseEstacionesMap(raw: unknown): Record<string, string[]> {
  if (!isRecord(raw)) return {};
  const result: Record<string, string[]> = {};
  for (const [slug, saved] of Object.entries(raw)) {
    if (!Array.isArray(saved)) continue;
    defineOwn(result, slug, saved.filter(isStationRefString));
  }
  return result;
}

/**
 * Parses and validates the persisted `mb_user` value into a known
 * `UserPreferences` shape. Malformed JSON, non-object values, and unknown
 * services resolve to null; invalid fields fall back to safe defaults.
 */
export function parseUserPreferences(raw: unknown): UserPreferences | null {
  const data = parseStoredJson(raw);
  if (!isRecord(data)) return null;

  const servicio = validateServicioSlug(data.servicio);
  if (!servicio) return null;

  return {
    estaciones: parseEstacionesMap(data.estaciones),
    limite:
      typeof data.limite === 'number' && Number.isFinite(data.limite)
        ? data.limite
        : 10,
    servicio,
    theme: data.theme === 'light' ? 'light' : 'dark',
  };
}

/** A station segment must be a non-empty string that cannot escape the route. */
function validateStationRef(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const ref = value.trim();
  if (!ref || ref === '.' || ref === '..') return null;
  if (/[/\\\s]/.test(ref)) return null;
  return ref;
}

/** Extracts safe saved station refs before service metadata is available. */
export function getSavedStationRefs(
  raw: unknown,
  servicio: TServiciosSlug
): string[] {
  const data = parseStoredJson(raw);
  if (!isRecord(data) || !Array.isArray(data[servicio])) return [];

  return Array.from(
    new Set(
      data[servicio]
        .map(validateStationRef)
        .filter((ref): ref is string => Boolean(ref))
    )
  );
}

/**
 * Extracts the saved station refs (slugs or ids) for `servicio` from the
 * `mb_estaciones` map, keeping only non-empty refs present in the given
 * station metadata.
 */
export function getSavedEstaciones(
  estacionesMap: unknown,
  servicio: TServiciosSlug,
  estaciones: readonly StationRef[]
): string[] {
  const data = parseStoredJson(estacionesMap);
  if (!isRecord(data)) return [];
  const saved = data[servicio];
  if (!Array.isArray(saved)) return [];

  const knownStationRefs = new Set<string>(
    estaciones.flatMap((estacion) =>
      [String(estacion._id), estacion.slug].filter((ref): ref is string =>
        Boolean(ref)
      )
    )
  );

  return saved
    .filter(isStationRefString)
    .filter((ref) => knownStationRefs.has(ref));
}

/**
 * Resolves saved station ids or slugs to the canonical slugs from the active
 * service metadata. Unknown refs are discarded before they can become route
 * segments.
 */
export function getCanonicalEstacionSlugs(
  estacionesMap: unknown,
  servicio: TServiciosSlug,
  estaciones: readonly StationRef[]
): string[] {
  const canonicalByRef = new Map<string, string>(
    estaciones.flatMap((estacion): [string, string][] => {
      const slug =
        estacion.slug ||
        slugFromStationName(estacion.nom || estacion.name || '');
      return slug
        ? [
            [String(estacion._id), slug],
            [slug, slug],
          ]
        : [];
    })
  );

  return getSavedEstaciones(estacionesMap, servicio, estaciones)
    .map((ref) => canonicalByRef.get(ref))
    .filter((slug): slug is string => Boolean(slug))
    .filter((slug, index, values) => values.indexOf(slug) === index);
}

/** Builds a canonical route from the first two valid saved stations. */
export function buildSavedStationRoute(
  estacionesMap: unknown,
  servicio: TServiciosSlug,
  estaciones: readonly StationRef[]
): string | null {
  const stationSlugs = getCanonicalEstacionSlugs(
    estacionesMap,
    servicio,
    estaciones
  );

  if (stationSlugs.length < 2) return null;

  return buildRouteTarget(servicio, stationSlugs[0], stationSlugs[1]);
}

/**
 * Builds a route target from validated inputs only: `/${servicio}` when no
 * stations are available, otherwise `/${servicio}/${salida}/${llegada}`.
 * Returns null when there is no valid service.
 */
export function buildRouteTarget(
  servicio: TServiciosSlug | null | undefined,
  salida?: string | null,
  llegada?: string | null
): string | null {
  if (!servicio) return null;

  const salidaRef = validateStationRef(salida);
  const llegadaRef = validateStationRef(llegada);

  if (salidaRef && llegadaRef) {
    return `/${servicio}/${salidaRef}/${llegadaRef}`;
  }

  return `/${servicio}`;
}

/**
 * Serializes preferences to the exact cookie-value format used by `mb_user`.
 * Cookie attributes (name, max-age, path, etc.) are not included.
 */
export function encodeUserPreferences(
  preferences: StoredUserPreferences
): string {
  return encodeURIComponent(JSON.stringify(preferences));
}

/**
 * Returns the encoded byte size of a preferences value as it would be stored
 * in the cookie. The calculation matches the persistence contract:
 * `encodeURIComponent(JSON.stringify(preferences)).length`.
 */
export function getUserPreferencesCookieSize(
  preferences: StoredUserPreferences
): number {
  return encodeUserPreferences(preferences).length;
}

function dedupeStrings(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

/**
 * True when `key` is an own property of `object`. Calling the prototype
 * method directly keeps reserved keys (`toString`, `constructor`,
 * `__proto__`) from resolving through the prototype chain, so lookups never
 * throw and never leak inherited values.
 */
function hasOwn(object: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Defines `key` on `target` as an own data property instead of assigning
 * through the `__proto__` accessor, so reserved keys become plain data
 * properties rather than mutating the target's prototype.
 */
function defineOwn<T>(target: Record<string, T>, key: string, value: T): void {
  Object.defineProperty(target, key, {
    configurable: true,
    enumerable: true,
    value,
    writable: true,
  });
}

/**
 * Normalizes a raw, untrusted value into a safe `StoredUserPreferences` shape
 * without enforcing the size limit. Malformed fields fall back to safe
 * defaults; unknown fields are ignored. Returns null when the input cannot be
 * interpreted as an object.
 */
export function normalizeUserPreferences(
  raw: unknown
): StoredUserPreferences | null {
  const data = parseStoredJson(raw);
  if (!isRecord(data)) return null;

  const servicio = validateServicioSlug(data.servicio);
  const theme =
    data.theme === 'light' || data.theme === 'dark'
      ? data.theme
      : DEFAULT_THEME;
  const limite =
    typeof data.limite === 'number' && Number.isFinite(data.limite)
      ? data.limite
      : DEFAULT_LIMIT;

  const estaciones: Record<string, string[]> = {};
  const rawEstaciones = isRecord(data.estaciones) ? data.estaciones : {};

  for (const [slug, saved] of Object.entries(rawEstaciones)) {
    if (!Array.isArray(saved)) continue;
    const refs = dedupeStrings(saved.filter(isStationRefString));
    if (refs.length > 0) defineOwn(estaciones, slug, refs);
  }

  return {
    estaciones,
    limite,
    servicio,
    theme,
  };
}

/**
 * Parses and validates legacy localStorage `mb_estaciones` data.
 * Accepts a raw JSON string or an already-parsed value. Returns a normalized
 * per-service station map, or null when the input cannot be interpreted as a
 * valid object. Malformed entries are filtered out and reserved keys are
 * stored as own data properties without polluting the prototype.
 */
export function parseLegacyEstaciones(
  raw: unknown
): Record<string, string[]> | null {
  const data = parseStoredJson(raw);
  if (!isRecord(data)) return null;

  const result: Record<string, string[]> = {};

  for (const [slug, saved] of Object.entries(data)) {
    if (slug.length === 0) continue;
    if (!Array.isArray(saved)) continue;
    const refs = dedupeStrings(saved.filter(isStationRefString));
    if (refs.length > 0) defineOwn(result, slug, refs);
  }

  return result;
}

/**
 * Merges legacy localStorage station data into the cookie's `estaciones` map
 * without losing valid entries. Always returns a fresh, normalized result:
 * cookie stations come first (filtered, deduplicated, and copied per
 * service), legacy stations are appended after, skipping any refs already
 * present. Raw JSON strings are parsed through `parseLegacyEstaciones`;
 * malformed legacy input is ignored silently. Empty service arrays are
 * omitted and reserved keys (`toString`, `constructor`, `__proto__`) are
 * handled as plain own data properties without throwing or polluting the
 * prototype.
 */
export function mergeLegacyStations(
  cookieEstaciones: Record<string, string[]> | undefined,
  legacyEstaciones: unknown
): Record<string, string[]> {
  const merged: Record<string, string[]> = {};

  // Always start from a fresh, normalized cookie map: invalid refs are
  // filtered, duplicates removed, and empty service arrays omitted. The
  // input itself is never mutated or returned by reference.
  const base: Record<string, unknown> = isRecord(cookieEstaciones)
    ? cookieEstaciones
    : {};
  for (const [slug, saved] of Object.entries(base)) {
    if (slug.length === 0) continue;
    if (!Array.isArray(saved)) continue;
    const refs = dedupeStrings(saved.filter(isStationRefString));
    if (refs.length > 0) defineOwn(merged, slug, refs);
  }

  // Append legacy stations that are not already present. Strings are parsed
  // as raw JSON by parseLegacyEstaciones; invalid input (null, non-object,
  // malformed JSON) yields null and is ignored.
  const legacy = parseLegacyEstaciones(legacyEstaciones);
  if (!legacy) return merged;

  for (const [slug, refs] of Object.entries(legacy)) {
    const existing = hasOwn(merged, slug) ? merged[slug] : undefined;
    if (!existing) {
      defineOwn(merged, slug, refs);
      continue;
    }

    const known = new Set<string>(existing);
    for (const ref of refs) {
      if (!known.has(ref)) {
        existing.push(ref);
        known.add(ref);
      }
    }
  }

  return merged;
}

/**
 * Flattens a per-service station map into `(service, station)` tuples in FIFO
 * order: service insertion order first, then array order within each service.
 */
export function flattenStations(
  estaciones: Record<string, string[]>
): Array<[string, string]> {
  const tuples: Array<[string, string]> = [];

  for (const [service, stations] of Object.entries(estaciones)) {
    for (const station of stations) {
      tuples.push([service, station]);
    }
  }

  return tuples;
}

/**
 * Rebuilds a per-service station map from FIFO-ordered tuples. Empty buckets
 * are omitted because they are produced only when a service has no remaining
 * references. Reserved keys (`toString`, `constructor`, `__proto__`) become
 * plain own buckets without prototype mutation or inherited lookups.
 */
export function rebuildStationsFromTuples(
  tuples: Array<[string, string]>
): Record<string, string[]> {
  const estaciones: Record<string, string[]> = {};

  for (const [service, station] of tuples) {
    const bucket = hasOwn(estaciones, service)
      ? estaciones[service]
      : undefined;
    if (bucket) {
      bucket.push(station);
    } else {
      defineOwn(estaciones, service, [station]);
    }
  }

  return estaciones;
}

/**
 * Normalizes raw preferences and bounds them to `limit` bytes using FIFO
 * eviction of the oldest station references. Core fields (`servicio`, `theme`,
 * `limite`) are never evicted; if they alone exceed the limit the write is
 * rejected. Malformed input returns a failure result instead of throwing.
 */
export function fitUserPreferencesToCookie(
  raw: unknown,
  limit: number = MAX_COOKIE_SIZE
):
  | { success: true; value: StoredUserPreferences }
  | { success: false; reason: string } {
  const data = parseStoredJson(raw);
  if (!isRecord(data)) {
    return { success: false, reason: 'invalid input' };
  }

  const normalized = normalizeUserPreferences(data);
  if (!normalized) {
    return { success: false, reason: 'invalid input' };
  }

  const coreOnly: StoredUserPreferences = { ...normalized, estaciones: {} };
  if (getUserPreferencesCookieSize(coreOnly) > limit) {
    return { success: false, reason: 'core overflow' };
  }

  if (getUserPreferencesCookieSize(normalized) <= limit) {
    return { success: true, value: normalized };
  }

  let tuples = flattenStations(normalized.estaciones);
  while (
    tuples.length > 0 &&
    getUserPreferencesCookieSize({
      ...normalized,
      estaciones: rebuildStationsFromTuples(tuples),
    }) > limit
  ) {
    tuples = tuples.slice(1);
  }

  return {
    success: true,
    value: {
      ...normalized,
      estaciones: rebuildStationsFromTuples(tuples),
    },
  };
}
